import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  MdArrowBack, MdWarningAmber, MdAutoAwesome, MdEdit, MdUndo, MdClose,
} from 'react-icons/md';
import { AppContext } from '../contexts/AppContext';
import { useProfile } from '../contexts/ProfileContext';
import {
  fetchBudgetOverview, postBudgetConfig, previewAction, executeAction,
} from '../services/backendAPI';

// Keep the last overview so reopening the screen shows it instantly while a fresh
// fetch happens. Keyed on sessionId + customerId (sessionId is reused across
// profile switches, so it alone can't tell profiles apart).
let cachedOverview = { key: null, data: null };

const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

// `.ins-card` is a THEMED surface (`--bg-card`): white in the light (green) theme,
// dark navy in the dark (UGO/purple) theme. Text drawn directly on the card must
// use the themed inks so it stays legible in both — CARD_INK/CARD_MUTED flip with
// the theme, and BAR_TRACK is a muted themed track that colored fills sit on
// (Justin: dark track + colored fill, not a bright white bar).
const CARD_INK = 'var(--text-dark)';     // primary text on the card surface
const CARD_MUTED = 'var(--text-light)';  // secondary/muted text on the card
const BAR_TRACK = 'var(--border-color)'; // progress-bar track + hairline dividers

// Fixed dark inks for elements that carry their OWN light background regardless of
// theme — pills, the confirm card (forced white), the inline row editor (#f7f8fa).
const INK = '#1a1a1a';
const INK_MUTED = '#555555';
const TRACK = '#e0e0e0';
const ACCENT = '#2FBF00';

// Pace → bar color: over/near = red, warm if projected to exceed, green otherwise.
function paceColor(row) {
  if (row.limit == null) return '#9aa0a6';                    // no limit set — muted
  if (row.pct >= 100) return 'var(--td-red)';                  // already over
  if (row.projectedOver) return '#d68910';                    // on pace to exceed
  return ACCENT;                                              // comfortably under
}

// A small pill button used across the editors.
function PillButton({ onClick, children, tone = 'default', disabled }) {
  const tones = {
    default: { bg: '#f0f1f3', fg: INK, bd: TRACK },
    primary: { bg: ACCENT, fg: '#fff', bd: ACCENT },
    danger: { bg: '#fdecea', fg: '#8a1c12', bd: '#f5c6c0' },
    active: { bg: '#1a1a1a', fg: '#fff', bd: '#1a1a1a' },
  };
  const t = tones[tone] || tones.default;
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, borderRadius: 8,
      padding: '5px 10px', fontSize: 12, fontWeight: 700,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
    }}>
      {children}
    </button>
  );
}

function MonthOverviewCard({ month, summary, onSetTotalBudget, busy }) {
  const income = summary.income || 0;
  const spent = summary.spent || 0;
  const projected = summary.projectedSpend || 0;
  const totalBudget = summary.totalBudget;
  // Pace bar is measured against the total budget when one exists, else income.
  const base = totalBudget > 0 ? totalBudget : (income > 0 ? income : Math.max(spent, projected, 1));
  const spentPct = Math.min(100, Math.round((spent / base) * 100));
  const projPct = Math.min(100, Math.round((projected / base) * 100));
  const over = projected > base;

  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(totalBudget != null ? String(totalBudget) : '');

  const save = () => {
    const n = Number(val);
    onSetTotalBudget(n > 0 ? Math.round(n * 100) / 100 : null);
    setEditing(false);
  };

  return (
    <div className="ins-card">
      <div style={{ fontSize: 12, color: CARD_MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {month.label || 'This month'}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '2px 0 12px' }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: over ? 'var(--td-red)' : CARD_INK, lineHeight: 1.1 }}>
          {money(summary.remainingToSpend)}
        </div>
        <div style={{ fontSize: 13, color: CARD_MUTED }}>left to spend</div>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: CARD_MUTED }}>
          {month.daysLeft} day{month.daysLeft !== 1 ? 's' : ''} left
        </div>
      </div>

      {/* Pace bar: spent (solid) + projected-to-month-end (lighter) vs. budget/income. */}
      <div style={{ position: 'relative', height: 10, borderRadius: 5, background: BAR_TRACK, overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 5,
          width: `${projPct}%`, background: over ? '#d6891055' : '#2FBF0033',
        }} />
        <div style={{
          position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 5,
          width: `${spentPct}%`, background: over ? '#c0392b' : ACCENT,
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: CARD_MUTED, marginTop: 6 }}>
        <span>{money(spent)} spent{totalBudget > 0 ? ` of ${money(totalBudget)} budget` : (income > 0 ? ` of ${money(income)} in` : '')}</span>
        <span>projected {money(projected)}</span>
      </div>

      {/* Set-a-monthly-budget control (total cap → set_total_budget). */}
      {!editing ? (
        <button type="button" onClick={() => { setVal(totalBudget != null ? String(totalBudget) : ''); setEditing(true); }} style={{
          marginTop: 10, background: 'none', border: `1px dashed ${BAR_TRACK}`, borderRadius: 8,
          padding: '6px 10px', fontSize: 12, fontWeight: 700, color: CARD_MUTED, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
          <MdEdit size={13} /> {totalBudget != null ? `Monthly budget ${money(totalBudget)}` : 'Set a monthly budget'}
        </button>
      ) : (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, color: CARD_MUTED }}>$</span>
          <input
            type="number" inputMode="decimal" value={val} autoFocus
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
            placeholder="e.g. 3000"
            style={{
              flex: 1, minWidth: 0, padding: '6px 8px', border: `1px solid ${BAR_TRACK}`,
              borderRadius: 8, fontSize: 13, color: CARD_INK, background: 'var(--bg-light-gray)',
            }}
          />
          <PillButton tone="primary" onClick={save} disabled={busy}>Save</PillButton>
          {totalBudget != null && (
            <PillButton tone="danger" onClick={() => { onSetTotalBudget(null); setEditing(false); }} disabled={busy}>Remove</PillButton>
          )}
          <PillButton onClick={() => setEditing(false)}>Cancel</PillButton>
        </div>
      )}
    </div>
  );
}

function UpcomingBillsCard({ bills, totalDue, breach, onChatHandoff, onCoverBill, busy }) {
  if (!bills.length) return null;
  const hasBreach = bills.some((b) => !b.willClear);
  const askIva = () => {
    if (!onChatHandoff) return;
    const who = breach ? `${breach.name} (${money(breach.amount)}) on ${breach.dueDate}` : 'an upcoming bill';
    onChatHandoff(
      `Some upcoming bills might not clear — ${who} looks like it would overdraw my chequing. `
      + 'Can you help me cover it and walk me through the options?',
    );
  };

  return (
    <div className="ins-card" style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: CARD_INK }}>Upcoming bills</div>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: CARD_MUTED }}>
          {money(totalDue)} in 30 days
        </div>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {bills.map((b, i) => (
          <li key={`${b.name}-${b.dueDate}-${i}`} style={{
            display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 13,
            color: b.willClear ? CARD_INK : 'var(--td-red)', padding: '4px 0',
            borderTop: i ? `1px solid ${BAR_TRACK}` : 'none',
          }}>
            {!b.willClear && <span style={{ flexShrink: 0 }}>⚠</span>}
            <span style={{ fontWeight: b.willClear ? 400 : 700 }}>{b.name}</span>
            <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap', color: b.willClear ? CARD_MUTED : 'var(--td-red)' }}>
              {money(b.amount)} · {b.dueDate}
            </span>
          </li>
        ))}
      </ul>
      {hasBreach && (
        <div style={{
          marginTop: 10, padding: '8px 10px', borderRadius: 10, background: '#fdecea',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          <MdWarningAmber size={16} style={{ color: 'var(--td-red)', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#8a1c12', flex: 1, minWidth: 120 }}>
            A bill may overdraw your chequing before it clears.
          </span>
          {onCoverBill && (
            <button type="button" onClick={onCoverBill} disabled={busy} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
              background: '#c0392b', color: '#fff', border: 'none', borderRadius: 8,
              padding: '6px 10px', fontWeight: 700, fontSize: 12,
              cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
            }}>
              Cover this bill
            </button>
          )}
          {onChatHandoff && (
            <button type="button" onClick={askIva} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
              background: '#fff', color: '#c0392b', border: '1px solid #c0392b', borderRadius: 8,
              padding: '6px 10px', fontWeight: 700, fontSize: 12, cursor: 'pointer',
            }}>
              <MdAutoAwesome size={14} /> Ask IVA
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const CLASSES = [['need', 'Need'], ['want', 'Want'], ['debt', 'Debt']];

// The inline editor that opens under a category row: limit, feedback, guardrail,
// and (for over-pace rows) confirm-gated inline actions.
function CategoryEditor({ row, allCategories, onEvent, onUndo, onTrim, onCover, busy }) {
  const [limitVal, setLimitVal] = useState(row.limit != null ? String(row.limit) : '');
  const [showRecat, setShowRecat] = useState(false);
  const overPace = row.limit != null && (row.pct >= 100 || row.projectedOver);

  const saveLimit = () => {
    const n = Number(limitVal);
    if (n > 0) onEvent({ type: 'set_limit', category: row.category, limit: Math.round(n * 100) / 100 });
  };

  return (
    <div style={{
      marginTop: 8, padding: 10, borderRadius: 10, background: '#f7f8fa', border: `1px solid ${TRACK}`,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {/* Limit editor */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: INK_MUTED, textTransform: 'uppercase', marginBottom: 4 }}>Monthly limit</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, color: INK_MUTED }}>$</span>
          <input
            type="number" inputMode="decimal" value={limitVal}
            onChange={(e) => setLimitVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveLimit(); }}
            placeholder="set a limit"
            style={{ flex: 1, minWidth: 0, padding: '6px 8px', border: `1px solid ${TRACK}`, borderRadius: 8, fontSize: 13, color: INK, background: '#fff' }}
          />
          <PillButton tone="primary" onClick={saveLimit} disabled={busy}>Save</PillButton>
          {row.limit != null && (
            <PillButton tone="danger" onClick={() => onEvent({ type: 'clear_limit', category: row.category })} disabled={busy}>Remove limit</PillButton>
          )}
        </div>
      </div>

      {/* Feedback: recategorize / ignore / reclassify */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: INK_MUTED, textTransform: 'uppercase', marginBottom: 4 }}>Feedback</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <PillButton onClick={() => setShowRecat((s) => !s)} disabled={busy}>Recategorize…</PillButton>
          <PillButton tone="danger" onClick={() => onEvent({ type: 'ignore_txn', category: row.category })} disabled={busy}>Ignore this</PillButton>
          {CLASSES.map(([k, lbl]) => (
            <PillButton key={k} tone={row.class === k ? 'active' : 'default'}
              onClick={() => onEvent({ type: 'reclassify_txn', category: row.category, klass: k })} disabled={busy}>
              {lbl}
            </PillButton>
          ))}
        </div>
        {showRecat && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {allCategories.filter((c) => c !== row.category).map((c) => (
              <PillButton key={c} onClick={() => { onEvent({ type: 'recategorize', from: row.category, to: c }); setShowRecat(false); }} disabled={busy}>
                → {c}
              </PillButton>
            ))}
          </div>
        )}
      </div>

      {/* Guardrail alert threshold */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: INK_MUTED, textTransform: 'uppercase', marginBottom: 4 }}>Alert me at</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {[70, 80, 90].map((pct) => (
            <PillButton key={pct} tone={row.guardrail === pct ? 'active' : 'default'}
              onClick={() => onEvent({ type: 'set_guardrail', category: row.category, thresholdPct: pct })} disabled={busy || row.limit == null}>
              {pct}%
            </PillButton>
          ))}
          {row.guardrail && (
            <PillButton tone="danger" onClick={() => onEvent({ type: 'clear_guardrail', category: row.category })} disabled={busy}>Off</PillButton>
          )}
        </div>
        {row.limit == null && (
          <div style={{ fontSize: 11, color: '#9aa0a6', marginTop: 3 }}>Set a limit first to enable alerts.</div>
        )}
      </div>

      {/* Inline actions for an over-pace row */}
      {overPace && (
        <div style={{ borderTop: `1px solid ${TRACK}`, paddingTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <PillButton tone="danger" onClick={onCover} disabled={busy}>Cover a bill</PillButton>
          <PillButton onClick={() => onTrim(row.category)} disabled={busy}>Trim {row.category}</PillButton>
        </div>
      )}

      {row.edited && (
        <button type="button" onClick={() => onUndo(row)} disabled={busy} style={{
          alignSelf: 'flex-start', background: 'none', border: 'none', color: INK_MUTED,
          fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0,
        }}>
          <MdUndo size={13} /> Undo my changes
        </button>
      )}
    </div>
  );
}

function CategoriesCard({ categories, openCat, setOpenCat, editorProps }) {
  if (!categories.length) return null;
  const allCategories = categories.map((c) => c.category);
  return (
    <div className="ins-card" style={{ marginTop: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: CARD_INK, marginBottom: 10 }}>By category</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {categories.map((row) => {
          const noLimit = row.limit == null;
          const barPct = noLimit ? 0 : Math.min(100, row.pct || 0);
          const open = openCat === row.category;
          return (
            <div key={row.category}>
              <button type="button" onClick={() => setOpenCat(open ? null : row.category)} style={{
                display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4, alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 700, color: CARD_INK, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {row.category}
                    {row.edited && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#0a6ebd', background: '#e6f2fb', borderRadius: 6, padding: '1px 5px' }}>
                        adjusted by you
                      </span>
                    )}
                  </span>
                  <span style={{ color: CARD_MUTED, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {noLimit ? `${money(row.spent)} spent` : `${money(row.spent)} of ${money(row.limit)}`}
                    {open ? <MdClose size={14} /> : <MdEdit size={13} />}
                  </span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: BAR_TRACK, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 4, width: `${barPct}%`, background: paceColor(row) }} />
                </div>
                <div style={{ fontSize: 12, color: CARD_MUTED, marginTop: 3 }}>
                  {noLimit
                    ? 'no limit set — tap to add one'
                    : `${row.pct}%${typeof row.projected === 'number' && row.projected > row.limit ? ` · projected ${money(row.projected)}` : ''}`
                      + (row.guardrail ? ` · alert at ${row.guardrail}%` : '')
                      + (row.class ? ` · ${row.class}` : '')}
                </div>
              </button>
              {open && (
                <CategoryEditor row={row} allCategories={allCategories} {...editorProps} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const BudgetScreen = ({ onNavigate, onBack, onChatHandoff }) => {
  const { sessionId, sessionSummary, simDate } = useContext(AppContext);
  const { loadedProfileData } = useProfile();

  const customerId = sessionSummary?.customerId || null;
  const cacheKey = sessionId ? `${sessionId}:${customerId || ''}` : null;

  // Bypass the cache when sim is active — each simDate is a different view.
  const [data, setData] = useState(!simDate && cachedOverview.key === cacheKey ? cachedOverview.data : null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openCat, setOpenCat] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { type, params, summary }

  const store = useCallback((d, key) => {
    if (!simDate) cachedOverview = { key, data: d };
    setData(d);
  }, [simDate]);

  useEffect(() => {
    if (!sessionId) return;
    if (!simDate) setData(cachedOverview.key === cacheKey ? cachedOverview.data : null);
    let cancelled = false;
    setLoading(true);
    (async () => {
      const d = await fetchBudgetOverview(sessionId, simDate || null);
      if (!cancelled && d?.ready) store(d.overview, cacheKey);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [sessionId, cacheKey, store, simDate]);

  // Post a budget-config event, reconciling with the fresh overview the server returns.
  const onEvent = useCallback(async (event) => {
    if (!sessionId) return;
    setBusy(true);
    const res = await postBudgetConfig(sessionId, event);
    if (res?.ok && res.overview) store(res.overview, cacheKey);
    setBusy(false);
  }, [sessionId, cacheKey, store]);

  // Undo a row's edits by appending the inverse events (last write wins on replay).
  const onUndo = useCallback(async (row) => {
    // Clearing the limit + guardrail + reclass returns the row to its derived state.
    await onEvent({ type: 'clear_limit', category: row.category });
    await onEvent({ type: 'clear_guardrail', category: row.category });
  }, [onEvent]);

  const onTrim = useCallback((category) => {
    if (!onChatHandoff) return;
    onChatHandoff(
      `I'm over-pace on ${category} this month. Can you suggest concrete ways to trim my ${category} spending and set a realistic limit?`,
    );
  }, [onChatHandoff]);

  // Confirm-gated action: preview first (so the numbers reflect current state), then
  // show a confirm panel; on confirm, execute through the existing action → ledger path.
  const startCover = useCallback(async () => {
    if (!sessionId) return;
    setBusy(true);
    const pv = await previewAction(sessionId, 'cover_shortfall', {});
    setBusy(false);
    if (!pv?.ok) { setNotice(pv?.reason || 'Nothing to cover right now.'); return; }
    setConfirmAction({ type: 'cover_shortfall', params: pv.params, summary: pv.summary });
  }, [sessionId]);

  const doConfirm = useCallback(async () => {
    if (!confirmAction || !sessionId) return;
    setBusy(true);
    const res = await executeAction(sessionId, confirmAction.type, confirmAction.params);
    setBusy(false);
    setConfirmAction(null);
    if (res?.ok) {
      setNotice(res.summary || 'Done.');
      const d = await fetchBudgetOverview(sessionId);
      if (d?.ready) store(d.overview, cacheKey);
    } else {
      setNotice(res?.reason || 'Could not complete that action.');
    }
  }, [confirmAction, sessionId, cacheKey, store]);

  const profileName = loadedProfileData?.name;

  const editorProps = {
    onEvent, onUndo, onTrim, onCover: startCover, busy,
  };

  return (
    <div className="screen insights-screen bh-flow">
      <header className="bh-flow-hero">
        <div className="bh-flow-hero-left">
          <button className="bh-flow-back" onClick={onBack || (() => onNavigate && onNavigate('services'))} aria-label="Back">
            <MdArrowBack />
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <h1 className="bh-flow-title">Budget</h1>
            {profileName && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', fontWeight: 500, marginTop: 2 }}>{profileName}</div>
            )}
          </div>
        </div>
      </header>

      <div className="scroll-content gray-bg ins-scroll">
        <div style={{ fontSize: 12, color: 'var(--text-light)', textAlign: 'center', marginBottom: 2 }}>
          your month at a glance — tap anything to steer it
        </div>

        {!sessionId && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            Open a profile to see your budget.
          </div>
        )}

        {sessionId && !data && loading && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            IVA is adding up your month…
          </div>
        )}

        {sessionId && !data && !loading && (
          <div className="ins-card" style={{ textAlign: 'center', color: 'var(--text-light)', fontSize: 13 }}>
            Budget isn't available for this profile yet.
          </div>
        )}

        {notice && (
          <div className="ins-card" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: CARD_INK, flex: 1 }}>{notice}</span>
            <PillButton onClick={() => setNotice(null)}>Dismiss</PillButton>
          </div>
        )}

        {confirmAction && (
          <div className="ins-card" style={{ marginBottom: 10, border: '1px solid #f5c6c0', background: '#fff' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: INK, marginBottom: 6 }}>Confirm</div>
            <div style={{ fontSize: 13, color: INK_MUTED, marginBottom: 10 }}>{confirmAction.summary}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <PillButton tone="primary" onClick={doConfirm} disabled={busy}>Confirm</PillButton>
              <PillButton onClick={() => setConfirmAction(null)} disabled={busy}>Cancel</PillButton>
            </div>
          </div>
        )}

        {data && (
          <MonthOverviewCard
            month={data.month}
            summary={data.summary}
            busy={busy}
            onSetTotalBudget={(amount) => onEvent({ type: 'set_total_budget', amount })}
          />
        )}
        {data && (
          <UpcomingBillsCard
            bills={data.upcomingBills || []}
            totalDue={data.totalDue || 0}
            breach={data.breach}
            onChatHandoff={onChatHandoff}
            onCoverBill={startCover}
            busy={busy}
          />
        )}
        {data && (
          <CategoriesCard
            categories={data.categories || []}
            openCat={openCat}
            setOpenCat={setOpenCat}
            editorProps={editorProps}
          />
        )}
      </div>
    </div>
  );
};

export default BudgetScreen;
